/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import { Modal } from 'antd';
import classNames from 'classnames';
import Utils from 'formiojs/utils';
// import plugins if you need
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Form } from 'react-formio';
import { useTranslation } from 'react-i18next';
import {
    getCustomEventHandler,
    ModalState,
} from 'src/components/FormioForm/FormioCustomEventHandlersMap';
import { getFormioDOMEventHandler } from 'src/components/FormioForm/FormioDOMEventHandlersMap';
import {
    FormioFormProps,
    FormioFormSubmission,
} from 'src/components/FormioForm/FormioFormProps';
import { FormNormalizer } from 'src/components/FormioForm/FormNormalizer';
import { sanitizeConfig } from 'src/components/FormioForm/sanitizeConfig';
import { SubmissionNormalizer } from 'src/components/FormioForm/SubmissionNormalizer';
import { ImageGalleryWrapper } from 'src/components/ImageGalleryWrapper/ImageGalleryWrapper';
import { FormioCustomEvent } from 'src/core/Formio.types';
import { Router } from 'src/core/router/Router';
import { useDropdown } from 'src/hooks/useDropdown';
import { getLocalStorageUiSettings } from 'src/hooks/useUrlUiSettings';
import { SettingsStore } from 'src/stores/SettingsStore/SettingsStore';
import { UrlHelper } from 'src/utils/UrlHelper';
import styled from 'styled-components';

export function FormioForm<T = any>({
    readOnly = false,
    normalizeSubmission,
    form,
    ...props
}: FormioFormProps<T>) {
    const { submission, onSubmit, onCustomEvent, getForm, onChange } = props;

    const { i18n } = useTranslation();

    const formInstance = useRef<any>(null);
    const containerRef = useRef<any>(null);

    const searchQueries = Router.getQuerySearchParams();

    const parsedDataFromUrl = UrlHelper.urlSearchToObject(searchQueries);

    const localStorageUiSettings = getLocalStorageUiSettings();

    const transformedSubmission = {
        ...props,
        submission: {
            ...submission,
            data: {
                ...submission?.data,
                ...parsedDataFromUrl,
                env: {
                    ...SettingsStore.settings?.env,
                    ...localStorageUiSettings,
                },
            },
        },
    };

    useDropdown(containerRef);

    // Modal state management
    const [modalState, setModalState] = useState<ModalState>({
        visible: false,
        title: '',
        content: null,
        footer: null,
        width: 520,
    });

    // Modal control functions - memoized to prevent unnecessary re-renders
    const openModal = useCallback((modalConfig: Partial<ModalState>) => {
        setModalState((prev) => ({
            ...prev,
            visible: true,
            ...modalConfig,
        }));
    }, []);

    const closeModal = useCallback(() => {
        setModalState((prev) => ({
            ...prev,
            visible: false,
        }));
    }, []);

    const handleModalOk = useCallback(() => {
        if (modalState.onOk) {
            modalState.onOk();
        } else {
            closeModal();
        }
    }, [modalState.onOk, closeModal]);

    const handleModalCancel = useCallback(() => {
        if (modalState.onCancel) {
            modalState.onCancel();
        } else {
            closeModal();
        }
    }, [modalState.onCancel, closeModal]);

    const options = useMemo(() => {
        return {
            readOnly,
            ...sanitizeConfig,
            i18next: i18n,
            language: i18n.language,
            evalContext: {
                utils: Utils, // override utils
            },
        };
    }, [readOnly, i18n, i18n.language]);

    useEffect(() => {
        return () => {
            if (formInstance.current?.destroy) {
                formInstance.current.destroy(true);
                formInstance.current = null;
            }
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        };
    }, []);

    const renderedForm = useMemo(() => {
        if (!form) {
            return null;
        }

        return (
            <Form
                {...transformedSubmission}
                form={FormNormalizer.normalize(form)}
                ref={(form: any) => {
                    formInstance.current = form;
                    getForm && getForm(form);
                }}
                options={options}
                onSubmit={(submission: FormioFormSubmission) => {
                    const result = { ...submission };

                    result.data = SubmissionNormalizer.normalizeCrud(
                        formInstance.current,
                        result.data,
                    );

                    if (normalizeSubmission) {
                        result.data = result.data
                            ? SubmissionNormalizer.normalize(
                                  formInstance.current,
                                  result.data,
                              )
                            : {};
                    }

                    return onSubmit ? onSubmit(result) : Promise.resolve();
                }}
                onCustomEvent={(event: FormioCustomEvent<any>) => {
                    const eventCopy = { ...event };
                    const defaultCustomEventHandler = getCustomEventHandler(
                        event.type,
                    );
                    if (onCustomEvent) {
                        eventCopy.data = SubmissionNormalizer.normalizeCrud(
                            formInstance.current,
                            event.data,
                        );
                        onCustomEvent(eventCopy);
                    }
                    if (defaultCustomEventHandler) {
                        defaultCustomEventHandler(event, {
                            form,
                            ...props,
                            // Pass modal control functions to event handlers
                            openModal,
                            closeModal,
                            // Pass form instance for triggering events from modals
                            formInstance: formInstance.current,
                        });
                    }
                }}
                onChange={onChange}
                readOnly={false}
            />
        );
    }, [
        form,
        submission,
        onSubmit,
        onCustomEvent,
        options,
        openModal,
        closeModal,
    ]);

    return (
        <>
            <StyledFormWrapper
                onClick={(e: React.MouseEvent<HTMLHRElement>) => {
                    const customBehavior = getFormioDOMEventHandler(e.type);
                    if (customBehavior) {
                        customBehavior(e);
                    }
                }}
                ref={containerRef}
                key={i18n.language}
            >
                {renderedForm}
            </StyledFormWrapper>
            <ImageGalleryWrapper containerRef={containerRef} />

            {/* Modal Component */}
            <Modal
                title={modalState.title}
                visible={modalState.visible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                footer={modalState.footer}
                width={modalState.width}
                destroyOnClose
                zIndex={1000}
                maskClosable
                centered
            >
                {modalState.content}
            </Modal>
        </>
    );
}
const StyledFormWrapper = styled.div.attrs((props) => {
    return {
        ...props,
        className: classNames(props.className, 'formio-container'),
    };
})`
    .radio-selected {
        pointer-events: none;
    }

    *:not(.formio-compoenent-form) .alert {
        display: none;
    }

    .builderComponent {
        .alert {
            display: block;
        }
    }
`;
